import { useMemo } from 'react'

import { useListSalesChannels } from '@/api/react-query'
import { useAppForm } from '@/components/form/form'
import { SalesChannelMultiSelect } from '@/components/sales-channel-multi-select'
import { AdvancedFilterDrawer } from '@/components/table/advanced-filter-drawer'
import { useI18n } from '@/i18n/i18n-react'

interface OrderFilters {
	salesChannels: string[]
	paymentStatus: string[]
	minAmount?: number
	maxAmount?: number
	fromDate?: number
	toDate?: number
}

interface OrderAdvancedFilterDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	defaultValues: OrderFilters
	onApply: (filters: OrderFilters) => void
}

export function OrderAdvancedFilterDrawer({
	open,
	onOpenChange,
	defaultValues,
	onApply
}: OrderAdvancedFilterDrawerProps) {
	const { L } = useI18n()
	const salesChannels = useListSalesChannels()

	const paymentStatusOptions = useMemo(
		() => [
			{ value: 'PAID', label: L.Enum.PaymentStatus('PAID') },
			{ value: 'PENDING', label: L.Enum.PaymentStatus('PENDING') },
			{ value: 'PARTIALLY_PAID', label: L.Enum.PaymentStatus('PARTIALLY_PAID') },
			{ value: 'PARTIALLY_REFUNDED', label: L.Enum.PaymentStatus('PARTIALLY_REFUNDED') },
			{ value: 'REFUNDED', label: L.Enum.PaymentStatus('REFUNDED') }
		],
		[L]
	)

	const form = useAppForm({
		defaultValues,
		onSubmit: async ({ value }) => {
			onApply(value)
			onOpenChange(false)
		}
	})

	return (
		<form.AppForm>
			<AdvancedFilterDrawer
				open={open}
				onOpenChange={onOpenChange}
				onClearAll={() => {
					const cleared: OrderFilters = {
						salesChannels: [],
						paymentStatus: [],
						minAmount: undefined,
						maxAmount: undefined,
						fromDate: undefined,
						toDate: undefined
					}
					onApply(cleared)
					onOpenChange(false)
				}}
				onApplyClick={() => form.handleSubmit()}
			>
				<form.AppField name='salesChannels'>
					{field => (
						<div className='flex flex-col gap-y-2'>
							<span className='txt-compact-small-plus text-ui-fg-subtle'>{L.Fields.SalesChannel()}</span>
							<SalesChannelMultiSelect
								salesChannels={salesChannels.data ?? []}
								value={field.state.value}
								onChange={uuids => field.handleChange(uuids)}
								allLabel={L.Analytics.BasketAnalysis.AllSalesChannels()}
								isLoading={salesChannels.isLoading}
							/>
						</div>
					)}
				</form.AppField>
				<form.AppField name='paymentStatus'>
					{field => (
						<field.ComboboxField label={L.Fields.PaymentStatus()} options={paymentStatusOptions} optional />
					)}
				</form.AppField>
				<form.AppField name='minAmount'>
					{field => <field.NumberField label={L.Orders.Filters.MinAmount()} optional />}
				</form.AppField>
				<form.AppField name='maxAmount'>
					{field => <field.NumberField label={L.Orders.Filters.MaxAmount()} optional />}
				</form.AppField>
				<form.AppField name='fromDate'>
					{field => <field.DateField label={L.Orders.Filters.FromDate()} optional />}
				</form.AppField>
				<form.AppField name='toDate'>
					{field => <field.DateField label={L.Orders.Filters.ToDate()} optional />}
				</form.AppField>
			</AdvancedFilterDrawer>
		</form.AppForm>
	)
}
